import { Badge, Button, Checkbox, Typography } from 'antd';
import { Link, useNavigate, useParams } from "react-router-dom";
import { useIsMutating } from "@tanstack/react-query";
import { useAuth } from "../../../providers/AuthProvider";
import React from "react";

const { Text } = Typography;

const InterviewStepDetail = ({ step, isActive, onPreview, onChange = async () => true, isDisabled, ...props }) => {

    const { auth, isUpdating, talentUpdate, update: updateAuth } = useAuth();
    const { currentStep, "*": other } = useParams();
    const requestsRunning = useIsMutating()

    const navigate = useNavigate();

    const goToStep = () => {
        if (props.goToStep) {
            return props.goToStep(step)
        }
        if (requestsRunning <= 0 && !isActive && !props.isDisabled) {
            return navigate(`/talent/application/video-interview/step/${step?.display_index}`, { replace: true })
        }
    }

    const onChangePublic = async (event) => {
        let talent_video_interviews = TB.map([...(auth.talent_video_interviews || [])], interview => {
            if (interview?.id === step.latest_interview?.id) {
                return {
                    ...interview,
                    is_public: event.target.checked
                }
            }
            return interview;
        });
        let updatedAuth = await talentUpdate({
            talent_video_interviews
        });
        if (updatedAuth) {
            await onChange({
                talent_video_interviews
            });
            await TB.queryClient.setQueryData(['fetchLatestVideoInterviewForTalent'], prevData => {
                if (!prevData || !Array.isArray(prevData)) return prevData;

                return TB.map([...prevData], stepData => {

                    if (stepData?.id === step.id && stepData.latest_interview) {
                        return { ...stepData, latest_interview: { ...stepData.latest_interview, is_public: event.target.checked } }
                    }
                    return stepData
                })
            })
            TB.toast('Profile Updated', {theme: 'success'});

        }
    }

    return (
        <div className="flex items-start flex-nowrap gap-4">
            <div className={TB.mapClass(`w-8 h-8 rounded-full flex items-center justify-center`, {
                'bg-secondary text-white': !TB.isDefined(currentStep) || isActive,
                'bg-gray-200 text-gray-400': TB.isDefined(currentStep) && !isActive,
            })} style={{ minWidth: '2rem', minHeight: '2rem' }}>
                {(currentStep && step.latest_interview) ? (
                    <i className=" fas fa-check"></i>
                ) : (
                    <span className=" font-semibold">{step.display_index}</span>
                )}
            </div>
            <div className={TB.mapClass("flex-grow items-stretch gap-3", {
                "flex flex-row flex-wrap md:flex-nowrap justify-between": !TB.isDefined(currentStep),
                "flex flex-col": TB.isDefined(currentStep),
                "opacity-90": TB.isDefined(currentStep) && !isActive,
            })}>
                <div className="flex flex-col items-stretch flex-grow">
                    <Text strong className={TB.mapClass("text-base", {
                        'clickable': TB.isDefined(currentStep) && requestsRunning <= 0 && !isActive && !props.isDisabled,
                        'pointer-events-none': !TB.isDefined(currentStep)
                    })} onClick={goToStep}>
                        {step.question}
                    </Text>
                    {step.description && (
                        <div className="text-xs text-gray-500 mt-2"
                            dangerouslySetInnerHTML={{ __html: step.description.replace(/<br\s*[\/]?>/gi, ". ") }} />
                    )}
                    {(!TB.isDefined(currentStep) || isActive) && (
                        <Text type="secondary text-xs mt-1.5">
                            Video response&nbsp; | &nbsp;{step.target_length_seconds} secs&nbsp; | &nbsp;
                            <Link
                                to={step.sample_video_path} target="_blank"
                                className="text-link text-link-info">
                                <i className="far fa-play-circle mr-1.5" />
                                <span>Play Example</span>
                            </Link>
                        </Text>
                    )}



                    {!TB.isDefined(currentStep) && auth.isApplicationApproved() && step.latest_interview && (
                        <div className="mt-3 border-t">
                            <p className="py-3 font-bold">Use of Video for Public Profile</p>

                            <div className="flex flex-col gap-3 items-start">
                                <div className="flex flex-row gap-3 items-center">
                                    {auth.isApplicationApproved() && !TB.isDefined(currentStep) && step.latest_interview && (
                                        <>
                                            {!step.latest_interview?.is_approved ? (
                                                step.latest_interview?.is_rejected ? (
                                                    <>
                                                        <div>
                                                            <Badge color={TB.config('color.danger')} count="Not Approved" className="opacity-80 text-xs" />
                                                        </div>
                                                        <p>This video is not approved for client view</p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div>
                                                            <Badge color={'#a1a1a1'} count="Pending Approval" className="opacity-80 text-xs italic" />
                                                        </div>
                                                        <p>This video is pending approval for client view</p>
                                                    </>
                                                )
                                            ) : (
                                                <>
                                                    <div>
                                                        <Badge color={TB.config('color.success')} count="Approved" className="text-xs" />
                                                    </div>
                                                    <p>This video is approved for client view</p>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="flex flex-row gap-2 items-center">
                                    <Checkbox
                                        className="text-xs whitespace-nowrap justify-self-center"
                                        checked={step.latest_interview?.is_public}
                                        disabled={isUpdating || !step.latest_interview?.is_approved}
                                        onChange={onChangePublic}
                                        id={`show-video-profile-${step.id}`}
                                    />
                                    <label
                                        htmlFor={`show-video-profile-${step.id}`}
                                        className={TB.mapClass(
                                            'text-xs font-light mt-1', {
                                                'text-[rgba(0,0,0,0.25)] cursor-not-allowed': isUpdating || !step.latest_interview?.is_approved,
                                                'clickable': !isUpdating && step.latest_interview?.is_approved
                                            })}
                                    >
                                        Yes, show this video on my public, client facing profile
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex flex-col items-stretch flex-1">
                    {step.latest_interview ? (
                        !isActive && (
                            <div
                                className={TB.isDefined(currentStep) ? "flex flex-row items-start gap-3" : "flex flex-col-reverse gap-2"}>

                                <div>
                                    <p className="text-xs font-medium mb-2 underline whitespace-nowrap">
                                        Your Submission
                                    </p>
                                    <div>
                                        <div className="flex flex-row gap-6 items-center">
                                            <div
                                                className="relative inline-flex items-start self-start flex-col bg-gray-200 rounded-lg overflow-hidden">
                                                <div className="min-w-52 w-52 relative min-h-[117px] h-[117px]">
                                                    <video src={step.latest_interview?.video_path}
                                                        controls={false}
                                                        width="100%" height="100%"
                                                        className="pointer-events-none rounded object-contain" />
                                                </div>


                                                <div
                                                    className="absolute inset-0 bg-black bg-opacity-20 flex flex-col items-center justify-center">
                                                    <i className="fas fa-play text-white opacity-70 fa-2x clickable"
                                                        onClick={() => {
                                                            onPreview(step.latest_interview?.video_path)
                                                        }} />
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>

                            </div>
                        )
                    ) : (

                        <div className="min-w-52 w-52 flex flex-col gap-3">
                            <p className="italic text-xs text-gray-400 font-medium mb-2 underline whitespace-nowrap">
                                Not started
                            </p>
                            {!TB.isDefined(currentStep) && auth.talent_video_interviews?.length > 0 && !isActive && (
                                <Button onClick={goToStep} disabled={props.isDisabled || requestsRunning > 0}
                                    type="default">
                                    Submit Recording
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>

        </div>
    )
};

export default InterviewStepDetail;